// React Imports
import { ReactElement, useEffect, useState } from "react";

// Third Party Libraries Imports
import moment from "moment";
import { ColumnsType } from "antd/es/table";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { ReactSVG } from "react-svg";

// Local Imports
import CustomTable from "../../../../components/custom-table/custom-table";
import WithdrawalService from "../../withdrawal.service";
import { WithdrawalGroups } from "../../../../utils/data/constant";
import { formatNumber } from "../../../../utils/helper/functions";
import CustomSearch from "src/components/custom-search/custom-search";

let searchTimer = null;

const Pending: React.FC = (): ReactElement => {
  const withdrawalService = new WithdrawalService();
  const navigate = useNavigate();
  const user = useSelector((state: any) => state.user);
  const loadingState = useSelector((state: any) => state?.loading);
  const [pendingRequestsList, setPendingRequestsList] = useState<any>([]);
  const [responseDate, setResponseDate] = useState<any>({});
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [searchWord, setSearchWord] = useState(null);

  useEffect(() => {
    fetchPendingListData();
  }, [searchWord,sortOrder, sortField]);

  const columns: ColumnsType<any> = [
    {
      title: "#",
      dataIndex: "id",
      key: "id",
      fixed: "left",
      width: 100,
      render: (_, __, index: number) => <span>{index + 1}</span>,
    },
    {
      title: "Client name",
      dataIndex: "name",
      key: "name",
      width: 170,
      sorter: true,
      render: (_, record) => (
        <span>
          {record?.bankAccount?.beneficiaryName ||
            record?.account?.activeKyc?.nameEn}
        </span>
      ),
    },
    {
      title: "Account #",
      dataIndex: "accountCode",
      key: "accountCode",
      width: 130,
      sorter: true,
      fixed: "left",
      render: (_, record) => (
        <Link
          to={`/home/business-account/${record?.portfolio?.account?.id}/${record?.portfolio?.account?.activeKycId}/details`}
        >
          {record?.portfolio?.account?.accountCode}
        </Link>
      ),
    },
    {
      title: "Wealth manager",
      dataIndex: "wealthManager",
      key: "wealthManager",
      render: (_, record) => record?.account?.wealthManager?.ae1 || "-",
    },
    {
      title: "Portfolio #",
      dataIndex: "portfolioCode",
      key: "portfolioCode",
      width: 130,
      sorter: true,
      fixed: "left",
      render: (_, record) => <span>{record?.portfolio?.accountCode}</span>,
    },
    {
      title: "Bank name",
      dataIndex: "bankName",
      key: "bankName",
      width: 200,
      sorter: true,
      render: (_, record) => <span>{record?.bankAccount?.bank?.bankName}</span>,
    },
    {
      title: "amount",
      dataIndex: "withdrawAmount",
      key: "withdrawAmount",
      width: 130,
      sorter: true,
      render: (text) => <span>{formatNumber(text)}</span>,
    },
    {
      title: "All cash",
      dataIndex: "fullAmount",
      key: "fullAmount",
      width: 130,
      render: (text) => <span>{text ? "Yes" : "No"}</span>,
    },
    {
      title: "Source",
      dataIndex: "source",
      key: "souce",
      render: (text) =>
        text === "Portal" ? (
          <ReactSVG src={"/images/monitor.svg"} />
        ) : (
          <ReactSVG src={"/images/mobile.svg"} />
        ),
    },
    {
      title: "Request Date",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 200,
      sorter: true,
      render: (text) => (
        <span className="date text-gray-500">
          {moment(text).format("DD MMM, YYYY hh:mm A")}
        </span>
      ),
    },
    {
      title: "Action",
      dataIndex: "action",
      width: 180,
      key: "action",
      render: (_, record) => (
        <ReactSVG
          src={"/images/take-action.svg"}
          onClick={() => {
            navigate(`/withdrawal/take-action/${record?.id}`);
          }}
        />
      ),
    },
  ];

  const onSearch = (e) => {
    clearTimeout(searchTimer);
    searchTimer = setTimeout(() => {
      setSearchWord(e.target.value);
    }, 1000);
  };

  const handleTableChange = (pagination, filters, sorter) => {
    setSortField(sorter.field);
    setSortOrder(sorter.order);
  };

  const fetchPendingListData = async (page = 1): Promise<void> => {
    const data: any = await withdrawalService.getWithdrawalListData(
      ["Pending"],
      page,
      searchWord,
      sortField,
      sortOrder
    );

    if (data !== undefined) {
      setResponseDate(data?.data);
      setPendingRequestsList(data?.data?.withdrawals);
    }
  };

  return (
    <div className=" mt-5 border-t-[1px] border-gray-400 rounded-lg">
      <div className="flex items-center justify-between rounded-lg bg-white py-[10px] px-5">
        <p className=" text-black-300 text-base font-bold">
          {!loadingState ? pendingRequestsList?.length : ""} Pending requests{" "}
          {user?.withdrawGroup
            ? `for ${WithdrawalGroups[user?.withdrawGroup]}`
            : ""}
        </p>
        <div className=" flex items-center">
          <CustomSearch onChange={(event) => onSearch(event)} /> 
        </div>
      </div>
      {pendingRequestsList?.length ? (
        <CustomTable
          columns={columns}
          data={pendingRequestsList}
          showPagination={true}
          pageSize={responseDate?.pageSize}
          totalCount={responseDate?.totalCount}
          page={responseDate?.currentPage}
          handleTableChange={handleTableChange}
          onPageChange={(page) => {
            fetchPendingListData(page);
          }}
        />
      ) : !loadingState ? (
        <div className="flex flex-col gap-3 w-full items-center justify-center mt-9">
          <img
            className="w-[40px] h-[40px]"
            src="/images/no-items.svg"
            alt="no data kamco"
          />
          <p className="text-lg text-gray-500 font-medium">
            There are no items in this list
          </p>
        </div>
      ) : (
        ""
      )}
    </div>
  );
};

export default Pending;
